/**
 * Table Search Component
 *
 * Reusable search input for filtering table records.
 * Can be used across different tables in the application.
 */

import React, { useState } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AddIcon from '@mui/icons-material/Add';
import { useTimezone } from '../../contexts/TimezoneContext';

export interface AdvancedFilters {
  owner: string;
  dateSort: 'none' | 'start-asc' | 'start-desc' | 'end-asc' | 'end-desc';
  seasonId: string;
  serviceType: string;
  destination: string;
  sectors: string;
}

interface TableSearchProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  filteredCount?: number;
  totalCount?: number;
  showCount?: boolean;
  onFiltersChange?: (filters: AdvancedFilters) => void;
  ownerOptions?: string[];
  seasonIdOptions?: string[];
  serviceTypeOptions?: string[];
  sectorOptions?: number[];
  destinationOptions?: string[];
  onAdd?: () => void;
  onAddAdhoc?: () => void;
}

const INITIAL_FILTERS: AdvancedFilters = {
  owner: '',
  dateSort: 'start-asc',
  seasonId: '',
  serviceType: '',
  destination: '',
  sectors: '',
};

export function TableSearch({
  value,
  onChange,
  placeholder = 'Search...',
  filteredCount,
  totalCount,
  showCount = true,
  onFiltersChange,
  ownerOptions = [],
  seasonIdOptions = [],
  serviceTypeOptions = [],
  sectorOptions = [],
  destinationOptions = [],
  onAdd,
  onAddAdhoc,
}: TableSearchProps) {
  const { timezone, toggleTimezone, isExternallyControlled } = useTimezone();
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<AdvancedFilters>(INITIAL_FILTERS);

  const handleFilterChange = (key: keyof AdvancedFilters, value: string) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    onFiltersChange?.(newFilters);
  };

  const handleClearFilters = () => {
    setFilters(INITIAL_FILTERS);
    onFiltersChange?.(INITIAL_FILTERS);
  };

  const hasActiveFilters = filters.owner || (filters.dateSort !== 'none' && filters.dateSort !== 'start-asc') || filters.seasonId || filters.serviceType || filters.destination || filters.sectors;

  return (
    <div className="border-b border-gray-200 bg-white search-bar-container">
      {/* Search Bar Row */}
      <div className="px-5 py-4 flex items-center gap-4">
        {/* Search Input */}
        <div className="flex-1 relative">
          <input
            type="text"
            placeholder={placeholder}
            value={value}
            onChange={e => onChange(e.target.value)}
            className="w-full pl-11 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-gray-900 text-lg outline-none
              focus:border-primary-500 focus:ring-3 focus:ring-primary-500/10 transition-colors"
            aria-label="Search"
          />
          <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
            <SearchIcon sx={{ fontSize: 20 }} />
          </span>
        </div>

        {/* Advanced Filter Button */}
        {onFiltersChange && (
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-4 py-2.5 border rounded-lg text-base font-medium cursor-pointer transition-all whitespace-nowrap
              ${hasActiveFilters
                ? 'bg-primary-500 text-white border-primary-500 hover:bg-primary-600'
                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`}
            aria-label="Advanced filters"
          >
            <FilterListIcon sx={{ fontSize: 20 }} />
            Advanced Filters
            {hasActiveFilters && (
              <span className="ml-1 px-1.5 py-0.5 bg-white/20 rounded text-xs font-bold">
                Active
              </span>
            )}
            {showFilters ? <ExpandLessIcon sx={{ fontSize: 20 }} /> : <ExpandMoreIcon sx={{ fontSize: 20 }} />}
          </button>
        )}

        {/* Timezone Toggle — hidden when controlled via URL path (/utc or /myt) */}
        {!isExternallyControlled && (
          <div
            className="flex items-center gap-2 cursor-pointer select-none"
            onClick={toggleTimezone}
            title={`Currently showing times in ${timezone}. Click to switch to ${timezone === 'UTC' ? 'MYT' : 'UTC'}.`}
          >
            <span className={`text-sm font-semibold ${timezone === 'UTC' ? 'text-gray-900' : 'text-gray-400'}`}>UTC</span>
            <div className="relative w-11 h-6 rounded-full transition-colors duration-200 bg-gray-200">
              <div className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-primary-500 shadow transition-transform duration-200
                ${timezone === 'MYT' ? 'translate-x-5' : 'translate-x-0'}`} />
            </div>
            <span className={`text-sm font-semibold ${timezone === 'MYT' ? 'text-gray-900' : 'text-gray-400'}`}>MYT</span>
          </div>
        )}

        {onAddAdhoc && (
          <button
            onClick={onAddAdhoc}
            className="flex items-center gap-2 px-4 py-2.5 bg-amber-500 border-none rounded-lg text-white text-base font-semibold cursor-pointer hover:bg-amber-600 shadow-sm whitespace-nowrap"
          >
            <AddIcon sx={{ fontSize: 20 }} />
            Create Adhoc
          </button>
        )}

        {onAdd && (
          <button
            onClick={onAdd}
            className="flex items-center gap-2 px-4 py-2.5 bg-primary-500 border-none rounded-lg text-white text-base font-semibold cursor-pointer hover:opacity-95 shadow-sm whitespace-nowrap"
          >
            <AddIcon sx={{ fontSize: 20 }} />
            Add Record
          </button>
        )}
      </div>

      {/* Expandable Filter Panel */}
      <div
        className={`transition-all duration-500 ease-in-out ${
          showFilters ? 'max-h-96 opacity-100 overflow-visible' : 'max-h-0 opacity-0 overflow-hidden'
        }`}
      >
        {onFiltersChange && (
          <div className="px-5 pb-4 border-t border-gray-200 bg-gray-50 search-filter-panel">
            {/* Filter Header */}
            <div className="py-3 flex items-center justify-between">
              <h3 className="text-base font-semibold text-gray-900">Filter Options</h3>
              {hasActiveFilters && (
                <button
                  onClick={handleClearFilters}
                  className="text-sm text-primary-500 hover:text-primary-600 font-medium"
                >
                  Clear All
                </button>
              )}
            </div>

            {/* Filter Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
            {/* Filter by Owner */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Filter by Owner
              </label>
              <select
                value={filters.owner}
                onChange={(e) => handleFilterChange('owner', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">All Owners</option>
                {ownerOptions.map(owner => (
                  <option key={owner} value={owner}>{owner}</option>
                ))}
              </select>
            </div>

            {/* Sort by Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Sort by Date
              </label>
              <select
                value={filters.dateSort}
                onChange={(e) => handleFilterChange('dateSort', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="none">No Sorting</option>
                <option value="start-asc">Start Date (Earliest First)</option>
                <option value="start-desc">Start Date (Latest First)</option>
                <option value="end-asc">End Date (Earliest First)</option>
                <option value="end-desc">End Date (Latest First)</option>
              </select>
            </div>

            {/* Filter by Season ID */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Filter by Season
              </label>
              <select
                value={filters.seasonId}
                onChange={(e) => handleFilterChange('seasonId', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">All Seasons</option>
                {seasonIdOptions.map(season => (
                  <option key={season} value={season}>{season}</option>
                ))}
              </select>
            </div>

            {/* Filter by Service Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Filter by Service
              </label>
              <select
                value={filters.serviceType}
                onChange={(e) => handleFilterChange('serviceType', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">All Services</option>
                {serviceTypeOptions.map(service => (
                  <option key={service} value={service}>{service}</option>
                ))}
              </select>
            </div>

            {/* Filter by Destination */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Filter by Destination
              </label>
              <select
                value={filters.destination}
                onChange={(e) => handleFilterChange('destination', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">All Destinations</option>
                {destinationOptions.map(dest => (
                  <option key={dest} value={dest}>{dest}</option>
                ))}
              </select>
            </div>

            {/* Filter by Sectors */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Filter by Sectors
              </label>
              <select
                value={filters.sectors}
                onChange={(e) => handleFilterChange('sectors', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">All Sectors</option>
                {sectorOptions.map(count => (
                  <option key={count} value={count}>{count} sector{count !== 1 ? 's' : ''}</option>
                ))}
              </select>
            </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
